import React, { useState } from "react";
import {
    Box,
    TextField,
    Button,
    Typography,
    Snackbar,
    Alert,
    Paper,
    Divider,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import axios from "axios";
import { apiUrl } from "./constant";
import logo from '../assets/logo.png';

const PatientRegisterQR = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const getTomorrowDate = () => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        return tomorrow.toISOString().split("T")[0];
    };

    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        age: "",
        contactNo: "",
        addressLine1: "",
        addressLine2: "",
        city: "",
        state: "",
        zipcode: "",
        startDate: getTomorrowDate(),
    });

    const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
    const [errors, setErrors] = useState({});

    const formatPhone = (value) => {
        const cleaned = value.replace(/\D/g, "").slice(0, 10);
        if (cleaned.length < 4) return cleaned;
        if (cleaned.length < 7) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
        return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        let updatedValue = value;

        if (name === "contactNo") {
            updatedValue = formatPhone(value);
            setFormData((prev) => ({
                ...prev,
                [name]: updatedValue,
            }));

            const rawNumber = updatedValue.replace(/\D/g, "");
            if (rawNumber.length > 0 && rawNumber.length <= 10) {
                setErrors((prevErrors) => ({
                    ...prevErrors,
                    [name]: "",
                }));
            }
        } else {
            setFormData((prev) => ({
                ...prev,
                [name]: updatedValue,
            }));

            if (updatedValue.trim() !== "") {
                setErrors((prevErrors) => ({
                    ...prevErrors,
                    [name]: "",
                }));
            }
        }
    };

    const validateForm = () => {
        const newErrors = {};
        const rawNumber = formData.contactNo.replace(/\D/g, "");

        if (!formData.firstName) newErrors.firstName = "First name is required";
        if (!formData.lastName) newErrors.lastName = "Last name is required";

        if (!formData.contactNo) {
            newErrors.contactNo = "Contact number is required";
        } else if (rawNumber.length !== 10) {
            newErrors.contactNo = "Phone number must be 10 digits";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        const payload = {
            ...formData,
            contactNo: `+1${formData.contactNo.replace(/\D/g, "")}`,
        };

        try {
            const response = await axios.post(`${apiUrl}/api/patients/add`, payload);
            if (response?.data?.message === "Patient with this contact number already exists.") {
                setSnack({
                    open: true,
                    message: "Patient with this contact number already exists.",
                    severity: "warning",
                });
                return;
            }

            setSnack({ open: true, message: "Patient added successfully!", severity: "success" });
            // setTimeout(() => navigate("/dashboard"), 1500);
        } catch (error) {
            setSnack({ open: true, message: "Failed to add patient", severity: "error" });
        }
    };

    return (
        <Box
            sx={{
                minHeight: "100vh",
                bgcolor: "#f8f6f2",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                px: 2,
                py: 4,
            }}
        >
            <Paper
                elevation={3}
                sx={{
                    width: "100%",
                    maxWidth: 600,
                    p: 4,
                    borderRadius: 4,
                    bgcolor: "#fff",
                    boxShadow:
                        "0 8px 16px rgba(0,0,0,0.12), 0 6px 20px rgba(0,0,0,0.08)",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        // mb: 3,
                    }}
                >
                    <img
                        src={logo}
                        alt="Logo"
                        style={{ height: 50, width: 'auto', marginRight: 16 }}
                    />
                    <Typography
                        variant="h4"
                        component="h1"
                        sx={{
                            fontWeight: 700,
                            color: "#7b6e4b",
                            fontSize: {
                                xs: "1rem",
                                sm: "1.5rem",
                                md: "2rem",
                                lg: "2rem",
                            },
                        }}
                    >
                        Patient Registration
                    </Typography>
                </Box>
                <hr />
                <Box
                    component="form"
                    noValidate
                    autoComplete="off"
                    onSubmit={handleSubmit}
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 3,
                        mt:3
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: isMobile ? "column" : "row",
                            gap: 2,
                        }}
                    >
                        <TextField
                            label="First Name *"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleChange}
                            error={!!errors.firstName}
                            helperText={errors.firstName}
                            fullWidth
                            autoFocus
                        />
                        <TextField
                            label="Last Name *"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleChange}
                            error={!!errors.lastName}
                            helperText={errors.lastName}
                            fullWidth
                        />
                    </Box>

                    {/* Age and Contact Number */}
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: isMobile ? "column" : "row",
                            gap: 2,
                        }}
                    >
                        <TextField
                            label="Age"
                            name="age"
                            value={formData.age}
                            onChange={handleChange}
                            type="number"
                            inputProps={{ min: 0 }}
                            fullWidth
                        />
                        <TextField
                            label="Contact Number *"
                            name="contactNo"
                            value={formData.contactNo}
                            onChange={handleChange}
                            placeholder="(123) 456-7890"
                            error={!!errors.contactNo}
                            helperText={errors.contactNo}
                            fullWidth
                        />
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    {/* Address lines */}
                    <TextField
                        label="Address Line 1"
                        name="addressLine1"
                        value={formData.addressLine1}
                        onChange={handleChange}
                        fullWidth
                    />
                    <TextField
                        label="Address Line 2"
                        name="addressLine2"
                        value={formData.addressLine2}
                        onChange={handleChange}
                        fullWidth
                    />

                    {/* City, State, Zip in one row or stacked */}
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: isMobile ? "column" : "row",
                            gap: 2,
                        }}
                    >
                        <TextField
                            label="City"
                            name="city"
                            value={formData.city}
                            onChange={handleChange}
                            fullWidth
                        />
                        <TextField
                            label="State"
                            name="state"
                            value={formData.state}
                            onChange={handleChange}
                            fullWidth
                        />
                        <TextField
                            label="Zip Code"
                            name="zipcode"
                            value={formData.zipcode}
                            onChange={handleChange}
                            fullWidth
                        />
                    </Box>

                    {/* Start Date */}
                    <TextField
                        label="Start Date"
                        name="startDate"
                        type="date"
                        value={formData.startDate}
                        onChange={handleChange}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: getTomorrowDate() }}
                        fullWidth
                        onClick={(e) => e.target.showPicker && e.target.showPicker()}
                    />

                    {/* Buttons */}
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: 2,
                            mt: 2,
                            flexDirection: isMobile ? "column" : "row",
                        }}
                    >
                        <Button
                            type="submit"
                            variant="contained"
                            sx={{
                                flex: 1,
                                bgcolor: "#7b6e4b",
                                fontWeight: 600,
                                borderRadius: 2,
                                "&:hover": {
                                    bgcolor: "#6a5c3d",
                                    boxShadow: "0 4px 10px rgb(78 52 46 / 0.5)",
                                },
                                transition: "all 0.3s ease",
                            }}
                        >
                            Save
                        </Button>
                    </Box>
                </Box>

                <Snackbar
                    open={snack.open}
                    autoHideDuration={4000}
                    onClose={() => setSnack({ ...snack, open: false })}
                    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                >
                    <Alert
                        severity={snack.severity}
                        variant="filled"
                        sx={{ width: "100%", fontWeight: 600 }}
                    >
                        {snack.message}
                    </Alert>
                </Snackbar>
            </Paper>
        </Box>
    );
};

export default PatientRegisterQR;
